import { ADJUSTMENT_SCHEMA } from './grade';

// Grok stage assist — shared plumbing for the "✨ Ask Grok" button in the
// guided stage workflow (LiveGradeStudio video Color tab + PhotoEditor).
// Builds a typed, per-stage parameter schema (dotted path, range/options,
// current value) that the backend forwards to Grok's vision model together
// with the stage's INPUT and CURRENT OUTPUT pane captures, so the model can
// judge what this stage should add and return concrete applyable values.

const NUM_BY_KEY = new Map(ADJUSTMENT_SCHEMA.map((s) => [s.key, s]));

// Discrete (segment/select) controls the numeric ADJUSTMENT_SCHEMA doesn't
// cover. Each option carries the UI-facing label EXACTLY as GradePanel renders
// it (profile chips, the input-science dropdown, the Seg segments) — the value
// is the internal id the grade stores. Grok is told to speak in labels while
// returning values, so its rationale never leaks internal ids the user has
// never seen (e.g. 'adobe-color' is labeled just "Color" everywhere in the UI).
const LEVELS = [
  { value: 'off', label: 'Off' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];
const SELECTS = {
  profile: {
    label: 'Profile',
    options: [
      { value: 'adobe-color', label: 'Color' },
      { value: 'neutral', label: 'Neutral' },
      { value: 'flat', label: 'Flat' },
    ],
    hint: 'base curve: "Color" bakes a ready contrast S-curve, "Neutral" is a pure de-log (honest canvas), "Flat" is extra-soft for heavy grading',
  },
  inputTransform: {
    label: 'Input color science',
    options: [
      { value: 'srgb', label: 'Rec.709 / sRGB (no transform)' },
      { value: 'log3g10', label: 'RED Log3G10 · Wide Gamut → 709 (IPP2)' },
      { value: 'slog3', label: 'Sony S-Log3 → 709' },
      { value: 'clog3', label: 'Canon C-Log3 → 709' },
      { value: 'vlog', label: 'Panasonic V-Log → 709' },
      { value: 'hlg', label: 'HLG → 709' },
      { value: 'linear', label: 'Linear → 709' },
    ],
    hint: 'what camera log/gamut the footage is; "no transform" = already display-ready Rec.709/sRGB',
  },
  'display.tonemap': {
    label: 'Tone map',
    options: LEVELS,
    hint: 'how hard scene-linear highlights are compressed into display range (log footage only)',
  },
  'display.rolloff': {
    label: 'Highlight roll-off',
    options: LEVELS,
    hint: 'extra soft knee just below white so spray/sun roll off instead of clipping',
  },
  'display.output': {
    label: 'Output',
    options: [
      { value: 'rec709', label: 'Rec.709' },
      { value: 'srgb', label: 'sRGB' },
    ],
    hint: 'display encoding: Rec.709 for video delivery, sRGB for stills',
  },
};

// UI label for a select option value ('adobe-color' → 'Color'); falls back to
// the raw value for anything unmapped.
export function selectOptionLabel(param, value) {
  const opt = (param?.options || []).find((o) => (o && typeof o === 'object' ? o.value : o) === value);
  return opt && typeof opt === 'object' ? opt.label : String(value);
}

const WHEELS = {
  'colorGrading.shadows': 'Shadows wheel',
  'colorGrading.midtones': 'Midtones wheel',
  'colorGrading.highlights': 'Highlights wheel',
  'colorGrading.global': 'Global wheel',
};

const CURVES = {
  'curve.master': {
    label: 'Tone curve',
    hint: 'master tone map; identity is the diagonal y=x; points {x,y} in 0..1 including endpoints; a gentle S adds contrast, lifting the bottom-left makes a matte film black',
  },
  'hsCurves.hueSat': {
    label: 'Hue → Sat',
    hint: 'x = hue 0..1 (0 red, 0.17 yellow, 0.33 green, 0.5 cyan, 0.67 blue, 0.83 magenta); y = 0.5 neutral, above boosts that hue\'s saturation; flat 0.5 line = no change',
  },
  'hsCurves.hueHue': {
    label: 'Hue → Hue',
    hint: 'x = hue 0..1; y = 0.5 neutral, above/below rotates that hue band; flat 0.5 line = no change',
  },
  'hsCurves.hueLum': {
    label: 'Hue → Lum',
    hint: 'x = hue 0..1; y = 0.5 neutral, above brightens / below darkens that hue band',
  },
  'hsCurves.satSat': {
    label: 'Sat → Sat',
    hint: 'x = source saturation 0..1; y = 0.5 neutral saturation multiplier',
  },
  'hsCurves.lumSat': {
    label: 'Luma → Sat',
    hint: 'x = luma 0..1; y = 0.5 neutral; pulling the shadow end down kills muddy color in the darks',
  },
};

const HSL_BAND_NAMES = ['Red', 'Orange', 'Yellow', 'Green', 'Aqua', 'Blue', 'Purple', 'Magenta'];
const hslPaths = [];
for (let i = 0; i < HSL_BAND_NAMES.length; i += 1) {
  hslPaths.push(`hsl.${i}.h`, `hsl.${i}.s`, `hsl.${i}.l`);
}

// The Grok-addressable parameters of each stage (dotted grade paths). Stages
// whose tools aren't parameter-shaped (the photo-only local masks) stay empty —
// the assist button hides there.
const STAGE_PARAM_PATHS = {
  decode: ['profile', 'inputTransform', 'display.exposure', 'display.tonemap', 'display.rolloff', 'display.output'],
  wb: ['wb.temp', 'wb.tint'],
  exposure: ['basic.exposure', 'basic.contrast', 'basic.whites', 'basic.blacks'],
  tone: ['basic.highlights', 'basic.shadows', 'curve.master'],
  hsl: hslPaths,
  wheels: Object.keys(WHEELS),
  curves: ['hsCurves.hueSat', 'hsCurves.hueHue', 'hsCurves.hueLum', 'hsCurves.satSat', 'hsCurves.lumSat'],
  local: [],
  detail: ['presence.texture', 'presence.clarity', 'presence.dehaze', 'presence.denoise', 'effects.sharpness'],
  vibrance: ['presence.vibrance', 'presence.saturation', 'presence.skinProtect'],
  effects: ['effects.vignette', 'effects.grain', 'effects.glow', 'effects.halation'],
  // The cross-stage "Match reference" pass: one request that may move a curated
  // set spanning several stages, so a reference correction lands on the control
  // that owns it instead of overloading whatever stage happens to be open.
  // `profile` is included because a reference is often nothing more than a
  // straight decode on a different base curve (Color's baked S vs Neutral's
  // flat de-log) — flipping the profile is then the whole match, and far
  // cleaner than hand-building the same S with contrast/curve moves.
  // Deliberately excluded: inputTransform (what the footage IS, not look),
  // per-band HSL and warper curves (too fine for a one-shot; the per-stage
  // assist handles them), and sharpness/denoise.
  'match-ref': [
    'profile',
    'display.exposure',
    'wb.temp', 'wb.tint',
    'basic.exposure', 'basic.contrast', 'basic.whites', 'basic.blacks',
    'basic.highlights', 'basic.shadows', 'curve.master',
    'colorGrading.shadows', 'colorGrading.midtones', 'colorGrading.highlights', 'colorGrading.global',
    'presence.dehaze', 'presence.vibrance', 'presence.saturation',
    'effects.glow', 'effects.halation', 'effects.grain', 'effects.vignette',
  ],
};

// Synthetic stage definition for the cross-stage reference-match pass. The
// backend switches to a dedicated system prompt on this id.
export const MATCH_REF_STAGE = {
  id: 'match-ref',
  name: 'Match reference (all stages)',
  goal:
    'Reproduce the reference grade in one coherent pass: place each correction on the control that '
    + 'owns it — white balance on temp/tint, tonal key and matte ceiling on exposure/contrast/whites/'
    + 'blacks and the tone curve, palette on the color wheels, intensity on vibrance/saturation, finish '
    + 'on the effects — with the smallest total set of moves that closes the measured gap.',
};

// "hsl.4.s" → ['hsl', 4, 's'] (numeric segments become array indices).
export const parseDottedPath = (dotted) => dotted.split('.').map((k) => (/^\d+$/.test(k) ? Number(k) : k));

export function getAtPath(obj, dotted) {
  let o = obj;
  for (const k of parseDottedPath(dotted)) {
    if (o == null) return undefined;
    o = o[k];
  }
  return o;
}

// Compact brief of the WHOLE stage pipeline (STAGES or VIDEO_STAGES) for the
// backend prompt: every sub-stage in render order with its goal and the
// controls it owns, the current one flagged. This is what lets Grok scope its
// moves to the stage at hand and leave other adjustments to their own stages.
export function pipelineBrief(stages, currentStageId) {
  return (Array.isArray(stages) ? stages : []).map((s) => ({
    id: s.id,
    name: s.name,
    current: s.id === currentStageId,
    goal: s.goal || '',
    controls: (s.tools || []).map((t) => t.label),
  }));
}

// Typed param descriptors for one stage, with current values read off `grade`.
// Shape: { path, label, type: 'number'|'select'|'wheel'|'curve', min, max,
// step, unit, options, hint, value, default }.
export function stageParamSchema(stageDef, grade) {
  const paths = STAGE_PARAM_PATHS[stageDef?.id] || [];
  const g = grade || {};
  const params = [];
  for (const path of paths) {
    if (SELECTS[path]) {
      const s = SELECTS[path];
      const cur = getAtPath(g, path);
      const value = cur == null ? s.options[0].value : cur;
      params.push({
        path,
        label: s.label,
        type: 'select',
        options: s.options,
        hint: s.hint,
        value,
        valueLabel: selectOptionLabel(s, value),
      });
    } else if (WHEELS[path]) {
      const cur = getAtPath(g, path) || { h: 0, s: 0, l: 0 };
      params.push({
        path,
        label: WHEELS[path],
        type: 'wheel',
        hint: 'h = hue 0..360°, s = tint strength -100..100 (usually 0..25 reads tasteful), l = lift/darken -100..100; {h:0,s:0,l:0} is neutral',
        value: { h: cur.h || 0, s: cur.s || 0, l: cur.l || 0 },
      });
    } else if (CURVES[path]) {
      const c = CURVES[path];
      const cur = getAtPath(g, path);
      params.push({
        path,
        label: c.label,
        type: 'curve',
        hint: c.hint,
        value: Array.isArray(cur) && cur.length ? cur : null,
      });
    } else {
      const s = NUM_BY_KEY.get(path);
      if (!s) continue;
      const cur = getAtPath(g, path);
      params.push({
        path,
        // The stage UI labels display.exposure "Log exposure" (the numeric
        // schema's 'Decode exposure' appears nowhere in the panel).
        label: path === 'display.exposure' ? 'Log exposure' : s.label,
        type: 'number',
        min: s.min,
        max: s.max,
        step: s.step,
        unit: s.unit || '',
        value: Number.isFinite(Number(cur)) ? Number(cur) : s.default,
        default: s.default,
      });
    }
  }
  return params;
}

// Compact human string for a suggested value (chip display). Select values
// render their UI label, never the internal id ('adobe-color' shows as "Color").
export function describeValue(param, value) {
  if (value == null) return 'no change';
  if (param?.type === 'select') return selectOptionLabel(param, value);
  if (param?.type === 'wheel' && typeof value === 'object') {
    return `${Math.round(value.h || 0)}° · s${Math.round(value.s || 0)} · l${Math.round(value.l || 0)}`;
  }
  if (Array.isArray(value)) return `${value.length}-pt curve`;
  if (typeof value === 'number') {
    const v = Math.abs(value) < 10 && !Number.isInteger(value) ? Math.round(value * 100) / 100 : Math.round(value * 10) / 10;
    return `${v > 0 ? '+' : ''}${v}${param?.unit ? ` ${param.unit}` : ''}`;
  }
  return String(value);
}

// Same-value check for the "✓ applied" chip state.
export function valueApplied(param, current, suggested) {
  if (typeof suggested === 'number') {
    const cur = Number(current);
    if (!Number.isFinite(cur)) return false;
    const tol = (param?.step || 1) / 2;
    return Math.abs(cur - suggested) <= tol;
  }
  return JSON.stringify(current ?? null) === JSON.stringify(suggested ?? null);
}

// Compact, objective "measured baseline" lines for the current stage, derived
// from the same histogram suggestions that power the auto chips (skipAuto
// taste entries excluded — only measured values travel). Sent with stage-assist
// requests so Grok anchors its moves to measurement instead of eyeballing a
// subtle cast off a compressed JPEG capture.
export function measurementLines(byPath) {
  if (!byPath) return null;
  const lines = [];
  for (const [path, s] of Object.entries(byPath)) {
    if (!s || s.skipAuto) continue;
    const val = s.value != null && typeof s.value === 'object'
      ? JSON.stringify(s.value)
      : `${s.value}${s.unit ? ` ${s.unit}` : ''}`;
    lines.push(`${path} → ${s.label || val}${s.why ? ` — ${s.why}` : ''}`);
  }
  return lines.length ? lines.slice(0, 12) : null;
}

// Grab a live WebGL pane (preserveDrawingBuffer canvas) as a downscaled JPEG
// data-URL for upload — ~1024px keeps a request's frames comfortably small
// while preserving the subtle near-neutral tints the refine rounds judge.
export function captureCanvasFrame(selector, scope = null, maxW = 1024) {
  const cv = (scope || document).querySelector(selector);
  if (!cv || !cv.width || !cv.height) return null;
  const s = Math.min(1, maxW / cv.width);
  const oc = document.createElement('canvas');
  oc.width = Math.max(1, Math.round(cv.width * s));
  oc.height = Math.max(1, Math.round(cv.height * s));
  const ctx = oc.getContext('2d');
  if (!ctx) return null;
  try {
    ctx.drawImage(cv, 0, 0, oc.width, oc.height);
    return oc.toDataURL('image/jpeg', 0.92);
  } catch {
    return null;
  }
}

// Objective stats of a captured frame (JPEG data-URL): luma percentiles,
// channel means and mean chroma, computed on a small resample. Both frames of
// a delta go through the same decode+resample, so the COMPARISON is honest
// even though JPEG itself shifts absolute values slightly.
async function frameStats(dataUrl) {
  const img = await new Promise((resolve, reject) => {
    const im = new Image();
    im.onload = () => resolve(im);
    im.onerror = () => reject(new Error('decode failed'));
    im.src = dataUrl;
  });
  const iw = img.naturalWidth || 1;
  const ih = img.naturalHeight || 1;
  const w = 200;
  const h = Math.max(1, Math.round((ih / iw) * w));
  const oc = document.createElement('canvas');
  oc.width = w;
  oc.height = h;
  const ctx = oc.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('no 2d context');
  ctx.drawImage(img, 0, 0, w, h);
  const d = ctx.getImageData(0, 0, w, h).data;
  const hist = new Array(256).fill(0);
  let r = 0;
  let g = 0;
  let b = 0;
  let chroma = 0;
  const n = w * h;
  for (let i = 0; i < d.length; i += 4) {
    const R = d[i];
    const G = d[i + 1];
    const B = d[i + 2];
    r += R; g += G; b += B;
    hist[Math.min(255, Math.round(0.2126 * R + 0.7152 * G + 0.0722 * B))] += 1;
    chroma += Math.max(R, G, B) - Math.min(R, G, B);
  }
  const pct = (q) => {
    let acc = 0;
    for (let v = 0; v < 256; v += 1) { acc += hist[v]; if (acc >= q * n) return v; }
    return 255;
  };
  return {
    p05: pct(0.05), p50: pct(0.5), p95: pct(0.95),
    r: Math.round(r / n), g: Math.round(g / n), b: Math.round(b / n),
    chroma: Math.round(chroma / n),
  };
}

// Measured output-vs-reference delta lines, one per paired sample — the numeric
// grounding that keeps a reference match honest (e.g. "whites 246 vs the ref's
// matte 170 ceiling" forbids blown foam no matter how the JPEGs read). Sent as
// `refDeltas` with any stage-assist / match-reference request that carries
// reference frames. Fails soft: any decode error just drops the lines.
export async function referenceDeltaLines(outputFrames, referenceFrames) {
  const n = Math.min(outputFrames?.length || 0, referenceFrames?.length || 0);
  if (!n) return null;
  const lines = [];
  for (let i = 0; i < n; i += 1) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const [out, ref] = await Promise.all([
        frameStats(outputFrames[i].dataUrl),
        frameStats(referenceFrames[i].dataUrl),
      ]);
      const d = (a, b) => (a - b > 0 ? `+${a - b}` : String(a - b));
      lines.push(
        `sample ${i + 1}: luma p05/p50/p95 OUTPUT ${out.p05}/${out.p50}/${out.p95} vs REFERENCE ${ref.p05}/${ref.p50}/${ref.p95}`
        + ` (Δ ${d(out.p05, ref.p05)}/${d(out.p50, ref.p50)}/${d(out.p95, ref.p95)})`
        + ` · mean RGB ${out.r},${out.g},${out.b} vs ${ref.r},${ref.g},${ref.b}`
        + ` · chroma ${out.chroma} vs ${ref.chroma} (Δ ${d(out.chroma, ref.chroma)})`
      );
    } catch { /* skip this pair */ }
  }
  return lines.length ? lines : null;
}

// Downscale any drawable (canvas or <img>/<video> with intrinsic size) to a
// JPEG data-URL — the reference-frame counterpart of captureCanvasFrame.
export function scaleToDataUrl(drawable, maxW = 1024, quality = 0.92) {
  const w = drawable.videoWidth || drawable.naturalWidth || drawable.width || 0;
  const h = drawable.videoHeight || drawable.naturalHeight || drawable.height || 0;
  if (!w || !h) return null;
  const s = Math.min(1, maxW / w);
  const oc = document.createElement('canvas');
  oc.width = Math.max(1, Math.round(w * s));
  oc.height = Math.max(1, Math.round(h * s));
  const ctx = oc.getContext('2d');
  if (!ctx) return null;
  try {
    ctx.drawImage(drawable, 0, 0, oc.width, oc.height);
    return oc.toDataURL('image/jpeg', quality);
  } catch {
    return null;
  }
}
